import React, { useEffect, useCallback, useRef } from 'react'
import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap'

import { useDashboardRedux, usePubsubRedux } from '@hooks'
import { usePubSub } from '@services'
import { storageHelpers } from '@utils'

const SettingsModal = ({ isOpen, toggle }) => {
    const { isLoading, cafeGroups, selectedGroupId, setSelectedGroupId, fetchCafeGroups } = useDashboardRedux()
    const { activeGroupId, setActiveGroupId, status, error } = usePubsubRedux()
    const { isConnected, isConnecting, connect, waitUntilConnected, joinGroup, leaveGroup, groups } = usePubSub()

    // Track previously joined group name to leave when switching
    const prevGroupRef = useRef(null)
    useEffect(() => {
        // keep ref in sync with first (and only) joined group, or computed from active selection
        const currentId = selectedGroupId || activeGroupId || null
        const currentGroup = currentId ? `pos-${currentId}` : null
        if (groups && groups.length > 0) {
            prevGroupRef.current = groups[0]
        } else {
            prevGroupRef.current = currentGroup
        }
    }, [groups, selectedGroupId, activeGroupId])

    const ensureConnectedAndJoin = useCallback(
        async cashRegisterId => {
            const targetGroup = `pos-${cashRegisterId}`
            try {
                // Always ensure a connection attempt is active
                if (!isConnected && !isConnecting) await connect()
                // Wait until the socket is actually connected (handles connecting state)
                await waitUntilConnected().catch(() => {})
                // If connected to a different group, leave it first
                const prev = prevGroupRef.current
                if (prev && prev !== targetGroup) {
                    try {
                        await leaveGroup(prev)
                    } catch {}
                }
                await joinGroup(targetGroup)
                prevGroupRef.current = targetGroup
            } catch (e) {
                // Swallow errors here; status badge will still reflect errors
                // Optionally, we could surface a toast/alert later
            }
        },
        [isConnected, isConnecting, connect, waitUntilConnected, joinGroup, leaveGroup]
    )

    // On open, fetch list only; do not preselect until list is available
    useEffect(() => {
        if (isOpen) {
            fetchCafeGroups()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // After list is loaded, validate stored ID and auto-join if valid
    useEffect(() => {
        if (!isOpen) return
        if (isLoading) return
        if (!Array.isArray(cafeGroups)) return

        try {
            const stored = storageHelpers.getCheckoutAssistantCashRegisterId()

            if (stored) {
                const exists = cafeGroups.some(cr => String(cr.id) === String(stored))
                if (exists) {
                    // Set selection if not already
                    if (String(selectedGroupId) !== String(stored)) {
                        setSelectedGroupId(stored)
                        setActiveGroupId(stored)
                    }
                    // Ensure actual join happens
                    ensureConnectedAndJoin(stored)
                } else {
                    // Stored id no longer exists — clear storage and selection, and leave any joined group
                    storageHelpers.clearCheckoutAssistantCashRegisterId()
                    if (selectedGroupId) setSelectedGroupId(null)
                    setActiveGroupId(null)
                    const joined = Array.isArray(groups) && groups.length > 0 ? groups[0] : null
                    if (joined) {
                        try {
                            leaveGroup(joined)
                        } catch {}
                    }
                    prevGroupRef.current = null
                }
            }
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isLoading, cafeGroups])

    const handleSelect = useCallback(
        async crId => {
            setSelectedGroupId(crId)
            setActiveGroupId(crId)
            try {
                storageHelpers.setCheckoutAssistantCashRegisterId(crId)
            } catch {}
            await ensureConnectedAndJoin(crId)
        },
        [setSelectedGroupId, setActiveGroupId, ensureConnectedAndJoin]
    )

    const currentId = selectedGroupId || activeGroupId || null
    const selectedGroupName = currentId ? `pos-${currentId}` : null
    const joinedGroupName = Array.isArray(groups) && groups.length > 0 ? groups[0] : null
    const isJoinedToSelected = !!selectedGroupName && joinedGroupName === selectedGroupName

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered keyboard backdrop size="lg">
            <ModalHeader toggle={toggle} tag="h1">
                Settings
            </ModalHeader>
            <ModalBody className="fs-4">
                {isLoading ?
                    <div className="d-flex align-items-center justify-content-center py-4">
                        <Spinner size="sm" color="secondary" className="me-3" />
                        <span className="text-secondary fs-4">Loading...</span>
                    </div>
                :   <div>
                        <p className="mb-3 text-secondary fs-4">Select a channel to connect.</p>
                        <div className="rounded p-0">
                            <div className="fw-semibold mb-3 fs-4">Channel List</div>
                            {Array.isArray(cafeGroups) && cafeGroups.length > 0 ?
                                <ul className="list-group">
                                    {cafeGroups.map((cr, idx) => {
                                        const selected = String(currentId) === String(cr.id)
                                        const crGroup = `pos-${cr.id}`
                                        const joined = joinedGroupName === crGroup
                                        return (
                                            <li
                                                key={cr.id}
                                                role="button"
                                                onClick={() => handleSelect(cr.id)}
                                                className={`list-group-item d-flex align-items-center justify-content-between py-4 ${selected ? 'active' : ''}`}
                                            >
                                                <div className="d-flex align-items-center fs-4">
                                                    {/* <span className="me-3">#{cr.id}</span> */}
                                                    <span className="me-3">#{idx + 1}</span>
                                                    <span className="fw-semibold">{cr.name}</span>
                                                </div>
                                                {/* <div className="d-flex align-items-center">
                                                    {joined && (
                                                        <span className="badge bg-success me-2 fs-5 py-2 px-3">Connected</span>
                                                    )}
                                                    {selected && !joined && (
                                                        <span className="badge bg-light text-primary fs-5 py-2 px-3">
                                                            Selected
                                                        </span>
                                                    )}
                                                </div> */}
                                            </li>
                                        )
                                    })}
                                </ul>
                            :   <div className="text-muted">No data available</div>}
                        </div>
                    </div>
                }
            </ModalBody>
        </Modal>
    )
}

export default SettingsModal
